import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { INTEGRATIONS } from "@/data/catalog";
import { SectionHeading } from "@/components/Sections";
import DemoFormDialog from "@/components/DemoFormDialog";
import { Button } from "@/components/ui/button";
import FinalCta from "@/components/FinalCta";
import { useSeo } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function IntegrationsPage() {
  useSeo({
    title: "Integrations – Shopify, Sheets, Lead Ads & API | GrabNChat",
    description: "Connect GrabNChat with Shopify, Google Sheets, Facebook & Instagram Lead Ads, Google Business Profile, Instagram, Messenger, Meta Ads, REST API and webhooks.",
  });
  const categories = [...new Set(INTEGRATIONS.map((i) => i.category))];
  return (
    <div data-testid="integrations-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10"><Breadcrumbs items={[{ name: "Integrations", path: "/integrations" }]} /></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-16 text-center">
          <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-4">Integrations</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">Connect WhatsApp to the tools you already use</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">Bring in leads, sync orders and reach customers on every Meta channel — or build your own integration with our API and webhooks.</p>
        </div>
      </section>

      {categories.map((cat, ci) => (
        <section key={cat} className={`py-16 ${ci % 2 ? "bg-slate-50" : ""}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title={cat} />
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {INTEGRATIONS.filter((i) => i.category === cat).map((it) => (
                <div key={it.slug} id={it.slug} className="scroll-mt-24 min-w-0 bg-white border border-slate-200 rounded-2xl p-6 flex flex-col" data-testid={`integration-${it.slug}`}>
                  <span className="w-12 h-12 rounded-xl grid place-items-center bg-slate-100 text-slate-800"><it.icon className="w-6 h-6" /></span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{it.name}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed flex-1">{it.desc}</p>
                  <Link to={it.link} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">Learn more <ArrowRight className="w-4 h-4" /></Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center bg-slate-50 border border-slate-200 rounded-3xl p-10">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Need a custom integration?</h2>
          <p className="mt-3 text-slate-600">Tell us which CRM, ERP or tool you use — we'll help you connect it with our REST API and webhooks.</p>
          <div className="mt-6">
            <DemoFormDialog source="integrations" defaultMessage="I need a custom integration with: " trigger={
              <Button className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-7 h-12 font-semibold">Talk to our team</Button>
            } />
          </div>
        </div>
      </section>
      <FinalCta />
    </div>
  );
}
